<template>
    <div
        :class="[
            compact ? 'ppv2-space-y-0' : 'ppv2-space-y-2',
            isOpen ? '!ppv2-opacity-100' : 'ppv2-opacity-70 hover:ppv2-opacity-100',
            classContainerAdded,
            { isNotrequired: !required },
        ]"
    >
        <BaseLabel
            v-if="label && !floatingLabel"
            :label="label"
            :for-id="id"
            :required="required"
            :customSizeLabel="customSizeLabel"
            :customClass="customLabelClass"
        />

        <InfoMessageBox v-if="tooltipMsg" class="ppv2-mt-2" :message="tooltipMsg" color="info" />

        <p v-if="description" class="ppv2-text-xs ppv2-text-gray-500">{{ description }}</p>

        <div class="ppv2-relative" :class="[customInputClass, { 'has-floating-label': floatingLabel && label }]">
            <!-- Floating Label -->
            <label
                v-if="floatingLabel && label"
                class="floating-label ppv2-text-opacity-70 hover:ppv2-text-opacity-100 poppinsMedium"
                :style="[
                    { fontSize: normalizeFontSize(customSizeLabel || 10, true) },
                    floatingLabelBg ? { backgroundColor: floatingLabelBg } : {},
                ]"
            >
                {{ label }}<span class="text-purple" v-if="required">*</span>
            </label>
            <!-- Left Icon -->
            <div
                v-if="leftIcon || $slots.leftIcon"
                class="ppv2-absolute ppv2-inset-y-0 ppv2-left-0 ppv2-pl-3 ppv2-flex ppv2-items-center ppv2-z-10"
            >
                <slot name="leftIcon">
                    <component v-if="leftIcon" :is="leftIcon" class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" />
                </slot>
            </div>

            <v-select
                ref="select"
                :id="id"
                v-bind="$attrs"
                v-model="internalValue"
                :options="options"
                :reduce="reduce ? reduce : (option) => (option ? option[optionValue] : null)"
                :get-option-label="
                    (option) => {
                        if (typeof option === 'string') return option
                        if (typeof optionLabel === 'function') return optionLabel(option)
                        return (
                            (option ? option[optionLabel] : '') || (option && (option.label || option.name || option.title)) || ''
                        )
                    }
                "
                :multiple="multiple"
                :clearable="clearable"
                :searchable="searchable"
                :taggable="taggable"
                :placeholder="placeholder"
                :disabled="disabled"
                :class="[
                    selectClasses,
                    dropdownPlacement === 'top' ? 'dropdown-top' : '',
                    compact ? 'v-select--compact' : '',
                    inCalendar ? 'v-select--inCalendar' : '',
                ]"
                :style="{ fontSize: normalizeFontSize(customSizeLabel, true) }"
                @open="handleOpen"
                @close="handleClose"
                v-on="$listeners"
                :append-to-body="appendToBody || fixedDropdown"
                :calculate-position="
                    fixedDropdown
                        ? (el, component) => {
                              if (!el.classList.contains('fixed-dropdown-menu')) {
                                  el.classList.add('fixed-dropdown-menu')
                                  el.classList.add('dropdown-' + id)
                              }
                              applyFixedDropdownPosition(el, component.$el)
                          }
                        : undefined
                "
                :dropdown-class="fixedDropdown ? `fixed-dropdown-menu dropdown-${id}` : ''"
            >
                <template v-slot:option="option">
                    <slot name="option" v-bind="option">
                        {{
                            typeof option === 'string'
                                ? option
                                : typeof optionLabel === 'function'
                                  ? optionLabel(option)
                                  : (option ? option[optionLabel] : '') ||
                                    (option && (option.label || option.name || option.title)) ||
                                    ''
                        }}
                    </slot>
                </template>

                <template v-slot:no-options="{ search, searching }">
                    <slot name="no-options" :search="search" :searching="searching">
                        <template v-if="searching">
                            Aucun résultat trouvé pour <em>{{ search }}</em
                            >.
                        </template>
                        <em v-else style="opacity: 0.5">Aucun élément n'est trouvé.</em>
                    </slot>
                </template>

                <template v-if="$scopedSlots['selected-option']" v-slot:selected-option="option">
                    <slot name="selected-option" v-bind="option" />
                </template>

                <!-- Icône personnalisée pour le dropdown -->
                <template #open-indicator v-if="isChevronsUpDown">
                    <ChevronsUpDown class="ppv2-w-4 ppv2-h-4 ppv2-text-black" />
                </template>
                <template #open-indicator v-else>
                    <ChevronDown class="ppv2-w-4 ppv2-h-4 ppv2-text-black" />
                </template>
            </v-select>
        </div>

        <p v-if="error" class="ppv2-text-xs ppv2-text-[#ed4b9e]">
            {{ Array.isArray(error) ? error[0] : error }}
        </p>
        <p v-if="helpText" class="ppv2-text-xs ppv2-text-gray-500">{{ helpText }}</p>

        <div
            v-if="showSelectionControls && multiple"
            class="ppv2-flex ppv2-items-center ppv2-justify-end ppv2-mt-2"
            :style="{ gap: normalizeSize(16, true) }"
        >
            <BaseButton
                type="button"
                variant="secondary"
                @click="allSelect"
                customClass="!ppv2-bg-opacity-30 !ppv2-min-w-[150px]"
                :customFontSize="12"
            >
                {{ selectAllText }}
            </BaseButton>
            <BaseButton
                type="button"
                variant="secondary"
                @click="allDeSelect"
                customClass="!ppv2-min-w-[150px]"
                :customFontSize="12"
            >
                {{ deselectAllText }}
            </BaseButton>
        </div>
    </div>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import BaseLabel from './BaseLabel.vue'
import InfoMessageBox from './InfoMessageBox.vue'
import BaseButton from './BaseButton.vue'
import { ChevronDown, ChevronsUpDown } from 'lucide-vue'
import normalizeSize from '../../utils/normalizeSize'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'BaseSelect',
    data() {
        return {
            isOpen: false,
            dropdownPosition: { top: '0px', left: '0px' },
        }
    },
    mounted() {
        if (this.fixedDropdown) {
            window.addEventListener('scroll', this.updateDropdownPosition, true)
            window.addEventListener('resize', this.updateDropdownPosition)
        }
    },
    beforeDestroy() {
        if (this.fixedDropdown) {
            window.removeEventListener('scroll', this.updateDropdownPosition, true)
            window.removeEventListener('resize', this.updateDropdownPosition)
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handleOpen() {
            this.isOpen = true
            if (this.fixedDropdown) {
                this.$nextTick(() => {
                    this.calculateDropdownPosition()
                })
            }
            this.$emit('open')
        },
        handleClose() {
            this.isOpen = false
            this.$emit('close')
        },
        openDropdown() {
            if (this.disabled || !this.$refs.select) return
            this.$refs.select.open = true
        },
        calculateDropdownPosition() {
            if (!this.$el) return
            const rect = this.$el.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
            }
        },
        /**
         * Positionne le menu en `position: fixed` sous (ou au-dessus) du champ.
         *
         * POINT UNIQUE de calcul, appelé à la fois par `calculate-position` (ouverture) et par
         * `updateDropdownPosition` (scroll/resize). La logique était auparavant dupliquée entre
         * les deux : n'en corriger qu'une ferait basculer le menu à l'ouverture puis le ramènerait
         * vers le bas au premier défilement.
         *
         * @param {HTMLElement} el     le menu déplacé dans <body> par `append-to-body`
         * @param {HTMLElement} anchor le champ servant de référence
         */
        applyFixedDropdownPosition(el, anchor) {
            if (!el || !anchor) return

            const rect = anchor.getBoundingClientRect()
            if (rect.width === 0) return

            const GAP = 4
            const MARGIN = 8 // respiration avec le bord de la fenêtre
            const MAX_HEIGHT = 350 // plafond déjà appliqué par .fixed-dropdown-menu
            const MIN_HEIGHT = 120 // ne jamais réduire le menu à presque rien

            el.style.setProperty('position', 'fixed', 'important')
            el.style.setProperty('left', rect.left + 'px', 'important')
            el.style.setProperty('width', rect.width + 'px', 'important')
            el.style.setProperty('z-index', '10000', 'important')

            const spaceBelow = window.innerHeight - rect.bottom - GAP - MARGIN
            const spaceAbove = rect.top - GAP - MARGIN

            // Sans `autoFlip`, comportement STRICTEMENT identique à l'historique : toujours vers le
            // bas, sans contrainte de hauteur inline. Les 27 autres écrans qui utilisent
            // `fixed-dropdown` passent par ici sans changement.
            if (!this.autoFlip) {
                el.style.setProperty('bottom', 'auto', 'important')
                el.style.setProperty('top', rect.bottom + GAP + 'px', 'important')
                return
            }

            // Hauteur souhaitée = contenu réel, plafonné. `scrollHeight` est disponible car le menu
            // est déjà dans le DOM quand cette méthode s'exécute.
            const desired = Math.min(el.scrollHeight || MAX_HEIGHT, MAX_HEIGHT)
            const openUp = spaceBelow < desired && spaceAbove > spaceBelow

            el.style.setProperty(
                'max-height',
                Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, openUp ? spaceAbove : spaceBelow)) + 'px',
                'important'
            )

            if (openUp) {
                // Ancrage par le bas : évite d'avoir à connaître la hauteur finale du menu, qui
                // varie avec le filtrage de la recherche.
                el.style.setProperty('top', 'auto', 'important')
                el.style.setProperty('bottom', window.innerHeight - rect.top + GAP + 'px', 'important')
            } else {
                el.style.setProperty('bottom', 'auto', 'important')
                el.style.setProperty('top', rect.bottom + GAP + 'px', 'important')
            }
        },
        updateDropdownPosition() {
            if (this.isOpen && this.fixedDropdown) {
                // v-select n'appelle `calculate-position` qu'à l'ouverture : il faut réappliquer
                // nous-mêmes pendant le scroll.
                const dropdown = document.querySelector(`.dropdown-${this.id}`)
                this.applyFixedDropdownPosition(dropdown, this.$el)
            }
        },
        allSelect() {
            if (!this.multiple) return
            const values = this.options.map((option) => {
                if (this.reduce) return this.reduce(option)
                if (option && typeof option === 'object' && this.optionValue in option) {
                    return option[this.optionValue]
                }
                return option
            })
            // Merge with existing selection (handles case where selected options are filtered out of the list)
            const existing = Array.isArray(this.internalValue) ? this.internalValue : []
            const merged = [...new Set([...existing, ...values])]
            this.internalValue = merged
        },
        allDeSelect() {
            if (!this.multiple) return
            this.internalValue = []
        },
    },
    components: {
        vSelect,
        BaseLabel,
        InfoMessageBox,
        BaseButton,
        ChevronDown,
        ChevronsUpDown,
    },
    model: {
        prop: 'value',
        event: 'input',
    },
    props: {
        id: { type: String, default: () => `select-${Math.random().toString(36).substr(2, 9)}` },
        value: { type: [String, Number, Object, Array], default: null },
        label: { type: String, default: '' },
        description: { type: String, default: '' },
        placeholder: { type: String, default: 'Sélectionner...' },
        options: { type: Array, required: true },
        optionLabel: { type: [String, Function], default: 'label' },
        optionValue: { type: String, default: 'value' },
        reduce: { type: Function, default: undefined },
        multiple: { type: Boolean, default: false },
        searchable: { type: Boolean, default: true },
        clearable: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        error: { type: [String, Array], default: '' },
        customInputClass: { type: String, default: '' },
        helpText: { type: String, default: '' },
        leftIcon: { type: String, default: '' },
        classAdded: { type: String, default: '' },
        customSizeLabel: { type: Number, default: 15 },
        customLabelClass: { type: String },
        tooltipMsg: { type: String, default: '' },
        isChevronsUpDown: { type: Boolean, default: true },
        dropdownPlacement: { type: String, default: 'bottom' }, // 'top' ou 'bottom'
        /**
         * Ouvre le menu vers le HAUT quand l'espace manque en bas, et plafonne sa hauteur à
         * l'espace réellement disponible.
         *
         * Opt-in (défaut `false`) : `fixed-dropdown` est utilisé par 28 écrans, et une bascule
         * imposée à tous changerait leur rendu sans demande. À n'activer que là où le champ peut
         * se retrouver bas dans la fenêtre — typiquement une modale.
         *
         * N'a d'effet qu'avec `fixed-dropdown` : sans lui, le menu reste positionné par vue-select.
         */
        autoFlip: { type: Boolean, default: false },
        taggable: { type: Boolean, default: false },
        showSelectionControls: { type: Boolean, default: false },
        selectAllText: { type: String, default: 'Tout sélectionner' },
        deselectAllText: { type: String, default: 'Tout déselectionner' },
        compact: { type: Boolean, default: false },
        inCalendar: { type: Boolean, default: false },
        classContainerAdded: {
            type: String,
            default: '',
        },
        floatingLabel: {
            type: Boolean,
            default: false,
        },
        floatingLabelBg: {
            type: String,
            default: '',
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
        fixedDropdown: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        internalValue: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        selectedOption() {
            if (this.multiple) {
                const valArray = Array.isArray(this.value) ? this.value : []
                return this.options.filter((opt) => valArray.includes(opt[this.optionValue]))
            }
            return this.options.find((opt) => String(opt[this.optionValue]) === String(this.value)) || null
        },
        selectClasses() {
            const base = ['ppv2-block', 'ppv2-w-full', 'ppv2-transition-colors', '!ppv2-bg-opacity-[3%]']

            if (this.$vuetify.theme.dark) {
                base.push('!ppv2-bg-white')
            } else {
                base.push('!ppv2-bg-black')
            }
            if (this.classAdded) {
                base.push(this.classAdded)
            }

            if (this.leftIcon || this.$slots.leftIcon) {
                base.push('ppv2-pl-10')
            } else {
                base.push('ppv2-pl-3')
            }
            base.push('ppv2-pr-3')
            return base.join(' ')
        },
    },
}
</script>

<style lang="scss" scoped>
.v-select {
    --vs-padding: 8.5px 0;
    --vs-border: 1px solid #ffffff !important;
    --vs-border-radius: 12px !important;
    --vs-font-size: inherit;
    --vs-color: #ffffff !important;
    --vs-placeholder-color: rgba(255, 255, 255, 0.8);
    background-color: transparent !important;
    border-radius: 12px;
    height: auto;
    min-height: 40px;
}
::v-deep .v-select .vs__search,
::v-deep .v-select .vs__search::placeholder {
    font-size: inherit;
}
.v-select.vs--multiple {
    height: auto !important;
}
.borderInput.v-select {
    border: 1px solid #9c9af880 !important;
}
.vs__open-indicator {
    fill: #ffffff !important;
    transform: rotate(0deg);
    transition: transform 0.2s ease;
    margin: 0 !important;
}
.vs__dropdown-option--highlight {
    background: #272c54 !important;
    color: var(--vs-dropdown-option--active-color);
}
.vs__actions {
    padding-top: 0 !important;
}
.v-select .vs__dropdown-toggle {
    height: auto;
    color: #ffffff !important;
    /*background-color: transparent !important;*/
    border-radius: 12px;
    min-height: 40px;
}

.v-select.vs--multiple .vs__dropdown-toggle {
    height: auto;
}

.vs__dropdown-toggle {
    border: none !important;
}

.has-floating-label {
    margin-top: 8px;
}

.floating-label {
    position: absolute;
    top: 0;
    transform: translateY(-65%);
    left: 12px;
    padding: 0 4px;
    z-index: 10;
    pointer-events: none;
    line-height: 1;
    opacity: 1 !important;
    white-space: nowrap;
    background-color: #fff;
    font-size: inherit;
}

.dark .floating-label {
    background-color: #060b28; /* Solid background for dark mode to cut the border */
}

.float-end {
    float: inline-end;
}

.vs__selected {
    background-color: #fff;
    border: solid 1px #94a3b8;
    border-radius: 8px;
    color: #ffffff !important;
    padding: 0 !important;
    margin: 0 !important;
    height: 100% !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: calc(100% - 30px);
}
.vs__dropdown-menu {
    min-width: 130px;
    background: rgba(0, 0, 0, 0.8) !important;
    color: white !important;
    border-radius: 10px !important;
    border: 1px solid #fff !important;
    top: calc(100% + 5px);
    padding: 0 !important;
    max-height: 200px;
}
.dark .vs__dropdown-menu {
    background: #141243 !important;
    color: white !important;
    border: 1px solid #fff !important;
}
.vs__deselect {
    fill: #021832;
    margin-left: 12px;
    margin-right: 5px;
}

/* Dropdown vers le haut */
::v-deep .dropdown-top .vs__dropdown-menu {
    top: auto !important;
    bottom: calc(100% + 5px) !important;
    z-index: 9999 !important;
}

/* Compact mode */
::v-deep .v-select.v-select--compact {
    min-height: 30px;
}
::v-deep .v-select.v-select--inCalendar {
    min-height: 22px;
}
::v-deep .v-select--compact .vs__dropdown-toggle {
    min-height: 30px !important;
    height: 30px !important;
    border-radius: 8px !important;
    padding: 0 !important;
}
::v-deep .v-select--inCalendar .vs__dropdown-toggle {
    min-height: 22px !important;
    height: 22px !important;
    border-radius: 8px !important;
    padding: 0 !important;
}
::v-deep .v-select--compact .vs__selected-options {
    flex-wrap: nowrap;
    overflow: hidden;
    padding: 0 0 0 4px !important;
}
::v-deep .v-select--compact .vs__selected {
    line-height: 28px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
::v-deep .v-select--inCalendar .vs__selected {
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
::v-deep .v-select--compact .vs__actions {
    padding: 0 4px 0 0 !important;
}
::v-deep .v-select--compact .vs__search {
    margin: 0 !important;
    padding: 0 !important;
    font-size: inherit;
}
::v-deep .v-select--inCalendar .vs__search {
    margin: 0 !important;
    padding: 0 !important;
    font-size: inherit;
}
::v-deep .v-select--compact .vs__search::placeholder {
    color: rgba(0, 0, 0, 0.45);
    font-size: inherit;
}
::v-deep .v-select--inCalendar .vs__search::placeholder {
    color: rgba(0, 0, 0, 0.45);
    font-size: inherit;
}
</style>
<style lang="scss">
/* Global styles for fixed dropdowns moved to body */
.fixed-dropdown-menu {
    position: fixed !important;
    background: white !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    border-radius: 12px !important;
    padding: 4px 0 !important;
    border: 1px solid #e2e8f0 !important;
    z-index: 10000 !important;
    max-height: 350px !important;
    overflow-y: auto !important;
    transform: none !important;
    margin: 0 !important;
}

.dark .fixed-dropdown-menu {
    background: #232d4b !important;
    border-color: #4a5568 !important;
}

.fixed-dropdown-menu .vs__dropdown-option {
    color: #4a5568 !important;
    padding: 8px 12px !important;
}

.dark .fixed-dropdown-menu .vs__dropdown-option {
    color: #bfbfbf !important;
}

.fixed-dropdown-menu .vs__dropdown-option--highlight {
    background: #f7fafc !important;
    color: #7d2dbd !important;
}

.dark .fixed-dropdown-menu .vs__dropdown-option--highlight {
    background: #1a1f37 !important;
    color: white !important;
}
</style>
